import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { Course } from "~/providers/course-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useCourse } from "~/hooks/use-course";
import { useOrderChartManager } from "~/managers/use-order-chart-manager";

export const Route = createFileRoute("/_main/settings/_layout/courses/")({
  component: Courses,
});

function Courses() {
  const { t } = useTranslation("common");
  const { openOrderChartDialog } = useOrderChartManager();
  const { courses } = useCourse();
  const columns: ColumnDef<Course>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: course }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(course.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("position"),
      accessorKey: "position",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("position")} />
      ),
      cell: ({ row: { original: course }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(course.position.toString(), globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("courses")}
        buttonLabel={capitalize(t("new_course"))}
        buttonAction={() => openOrderChartDialog()}
      />

      <DataTable
        defaultSorting={[{ id: t("position"), desc: false }]}
        columns={columns}
        data={courses}
        onSelect={openOrderChartDialog}
      />
    </>
  );
}
